import { useEffect, useState } from "react";
import { Mail, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminMessages, formatError } from "../api";
import { Card, PageHeader, Spinner } from "../ui";

const STATUS = ["new", "read", "replied", "archived"];

export default function Messages() {
  const [data, setData] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = (filter = statusFilter) =>
    adminMessages
      .list(filter ? { status_filter: filter, limit: 100 } : { limit: 100 })
      .then(setData)
      .catch(() => setData({ items: [], total: 0 }));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const select = async (m) => {
    try {
      const fresh = await adminMessages.get(m.id);
      setSelected(fresh);
      if (fresh.status === "new") {
        await adminMessages.patch(m.id, { status: "read" });
        load();
      }
    } catch (err) { toast.error(formatError(err)); }
  };

  const update = async (patch) => {
    if (!selected) return;
    try {
      const updated = await adminMessages.patch(selected.id, patch);
      setSelected(updated);
      load();
      toast.success("Saved.");
    } catch (err) { toast.error(formatError(err)); }
  };

  const remove = async () => {
    if (!selected) return;
    if (!window.confirm("Delete this message?")) return;
    try {
      await adminMessages.remove(selected.id);
      setSelected(null);
      load();
      toast.success("Deleted.");
    } catch (err) { toast.error(formatError(err)); }
  };

  if (!data) return <Spinner />;
  const items = data.items || [];

  return (
    <div data-testid="admin-messages">
      <PageHeader title="Messages" description="Contact form submissions from the public site." />

      <div className="mb-4 flex gap-2 flex-wrap">
        <button onClick={() => setStatusFilter("")} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${!statusFilter ? "bg-[hsl(var(--aoa-navy))] text-white" : "border border-slate-200 text-slate-600"}`}>All</button>
        {STATUS.map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)} data-testid={`filter-${s}`} className={`rounded-full px-3 py-1.5 text-xs font-semibold capitalize ${statusFilter === s ? "bg-[hsl(var(--aoa-navy))] text-white" : "border border-slate-200 text-slate-600"}`}>{s}</button>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <Card className="overflow-hidden">
          {items.length === 0 ? (
            <div className="p-12 text-center"><Mail className="h-8 w-8 mx-auto text-slate-300" /><p className="mt-3 text-sm text-slate-500">No messages.</p></div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((m) => (
                <li key={m.id}>
                  <button
                    onClick={() => select(m)}
                    data-testid={`message-row-${m.id}`}
                    className={`w-full text-left px-5 py-4 hover:bg-slate-50 ${selected?.id === m.id ? "bg-slate-50" : ""}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold text-[hsl(var(--aoa-navy))] truncate">{m.subject}</div>
                        <div className="text-xs text-slate-500 truncate">{m.name} · {m.email}</div>
                        <div className="text-[11px] text-slate-400 mt-0.5">{m.created_at ? new Date(m.created_at).toLocaleString() : ""}</div>
                      </div>
                      <span className={`flex-none rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${m.status === "new" ? "bg-[hsl(var(--aoa-royal))]/10 text-[hsl(var(--aoa-royal))]" : "bg-slate-100 text-slate-600"}`}>{m.status}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-6 sticky top-24 self-start" testId="message-detail">
          {selected ? (
            <>
              <div className="flex items-center justify-between">
                <h3 className="font-display text-lg font-semibold text-[hsl(var(--aoa-navy))]">{selected.subject}</h3>
                <button onClick={remove} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="mt-2 text-xs text-slate-500">
                {selected.name} · <a className="text-[hsl(var(--aoa-royal))] hover:underline" href={`mailto:${selected.email}`}>{selected.email}</a>
              </div>
              <div className="mt-1 text-[11px] text-slate-400">{selected.created_at ? new Date(selected.created_at).toLocaleString() : ""}</div>

              <p className="mt-5 whitespace-pre-wrap text-sm text-slate-700 leading-relaxed border-l-2 border-slate-200 pl-4">{selected.message}</p>

              <div className="mt-6">
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-700">Status</span>
                  <select value={selected.status} onChange={(e) => update({ status: e.target.value })} data-testid="message-status" className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
                    {STATUS.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
                  </select>
                </label>
              </div>

              <div className="mt-4">
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-700">Admin notes</span>
                  <textarea
                    rows={4}
                    value={selected.admin_notes || ""}
                    onChange={(e) => setSelected({ ...selected, admin_notes: e.target.value })}
                    onBlur={() => update({ admin_notes: selected.admin_notes || "" })}
                    data-testid="message-notes"
                    className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  />
                </label>
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-500">Select a message to view details.</p>
          )}
        </Card>
      </div>
    </div>
  );
}
